import type { SxProps } from '@mui/material'
import MuiInput from '@mui/material/Input'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/system'
import React, { forwardRef, useMemo } from 'react'
import NumberFormat from 'react-number-format'

import { fontFamily, LuneThemeNew } from '../theme'

import FieldValidationError from './FieldValidationError'

const { palette, spacing } = LuneThemeNew

type Spec = {
    bg: {
        default: string
        hover: string
        active: string
    }
    color: {
        default: string
        hover: string
        active: string
    }
    border: {
        default: string
        hover: string
        active: string
    }
}

const inputSpecs: Record<string, Spec> = {
    default: {
        bg: {
            default: palette.InputBg,
            hover: palette.InputBgHover,
            active: palette.InputBg,
        },
        color: {
            default: palette.TextPrimary,
            hover: palette.TextPrimary,
            active: palette.TextPrimary,
        },
        border: {
            default: palette.InputBorder,
            hover: palette.InputBorder,
            active: palette.InputBorderActive,
        },
    },
    inferred: {
        bg: {
            default: palette.InputBgInferred,
            hover: palette.InputBgInferredHover,
            active: palette.InputBgInferred,
        },
        color: {
            default: palette.TextPrimary,
            hover: palette.TextPrimary,
            active: palette.TextPrimary,
        },
        border: {
            default: 'transparent',
            hover: 'transparent',
            active: palette.InputBorderActive,
        },
    },
    error: {
        bg: {
            default: palette.InputBgError,
            hover: palette.InputBgErrorHover,
            active: palette.InputBgError,
        },
        color: {
            default: palette.TextPrimary,
            hover: palette.TextPrimary,
            active: palette.TextPrimary,
        },
        border: {
            default: 'transparent',
            hover: 'transparent',
            active: palette.InputBorderActive,
        },
    },
    disabled: {
        bg: {
            default: palette.InputBgDisabled,
            hover: palette.InputBgDisabled,
            active: palette.InputBgDisabled,
        },
        color: {
            default: palette.TextDisabled,
            hover: palette.TextDisabled,
            active: palette.TextDisabled,
        },
        border: {
            default: 'transparent',
            hover: 'transparent',
            active: 'transparent',
        },
    },
    readOnly: {
        bg: {
            default: palette.InputBgDisabled,
            hover: palette.InputBgDisabled,
            active: palette.InputBgDisabled,
        },
        color: {
            default: palette.TextPrimary,
            hover: palette.TextPrimary,
            active: palette.TextPrimary,
        },
        border: {
            default: 'transparent',
            hover: 'transparent',
            active: 'transparent',
        },
    },
}

/**
 * Generates base input styling sx object that can be reused by input-like components
 * @param size - Size of the input ('sm' or 'md')
 * @param wide - Whether the input should be full width
 * @param contentSelector - Selector for the inner content (e.g., 'input', '.MuiSelect-select')
 * @returns SxProps object with base input styles
 */
export const getInputBaseStyles = (
    size: 'sm' | 'md' = 'md',
    wide?: boolean,
    contentSelector: string = 'input',
) => {
    return LuneThemeNew.unstable_sx({
        fontFamily,
        px: spacing(1.5),
        py: spacing(1),
        height: size === 'md' ? spacing(6) : spacing(5),
        fontSize: size === 'md' ? spacing(2) : spacing(1.75),
        borderRadius: spacing(1),
        border: `1px solid`,
        svg: {
            height: spacing(2.5),
            width: spacing(2.5),
            color: `TextTertiary`,
            fill: `TextTertiary`,
        },

        ...(wide && {
            width: '100%',
        }),

        [contentSelector]: {
            fontSize: size === 'md' ? '16px' : '14px',
            padding: '0',
            lineHeight: size === 'md' ? '24px' : '20px',
            height: size === 'md' ? '24px' : '20px',
        },

        color: inputSpecs.default.color.default,
        '*::placeholder': {
            color: `${palette.TextPlaceholder} !important`,
            opacity: '1 !important',
        },

        // Default state
        backgroundColor: inputSpecs.default.bg.default,
        borderColor: inputSpecs.default.border.default,

        // Hover state
        '&:hover': {
            backgroundColor: inputSpecs.default.bg.hover,
            borderColor: inputSpecs.default.border.hover,
        },

        // Active/Focus state
        '&:focus-within': {
            backgroundColor: inputSpecs.default.bg.active,
            borderColor: inputSpecs.default.border.active,
        },

        // Inferred state
        '&.inferred': {
            backgroundColor: inputSpecs.inferred.bg.default,
            borderColor: inputSpecs.inferred.border.default,
            '&:hover': {
                backgroundColor: inputSpecs.inferred.bg.hover,
                borderColor: inputSpecs.inferred.border.hover,
            },
            '&:focus-within': {
                backgroundColor: inputSpecs.inferred.bg.active,
                borderColor: inputSpecs.inferred.border.active,
            },
        },

        // Error state
        '&.error': {
            backgroundColor: inputSpecs.error.bg.default,
            borderColor: inputSpecs.error.border.default,
            '&:hover': {
                backgroundColor: inputSpecs.error.bg.hover,
                borderColor: inputSpecs.error.border.hover,
            },
            '&:focus-within': {
                backgroundColor: inputSpecs.error.bg.active,
                borderColor: inputSpecs.error.border.active,
            },
        },

        // ReadOnly state
        '&.readOnly': {
            backgroundColor: inputSpecs.readOnly.bg.default,
            borderColor: inputSpecs.readOnly.border.default,
            [contentSelector]: {
                pointerEvents: 'none',
                color: inputSpecs.readOnly.color.default,
                textFillColor: `${inputSpecs.readOnly.color.default} !important`,
            },
            '*::placeholder': {
                color: `${inputSpecs.readOnly.color.default} !important`,
            },
        },

        // Disabled state
        '&.disabled': {
            backgroundColor: inputSpecs.disabled.bg.default,
            borderColor: inputSpecs.disabled.border.default,
            [contentSelector]: {
                pointerEvents: 'none',
                color: inputSpecs.disabled.color.default,
                textFillColor: `${inputSpecs.disabled.color.default} !important`,
                cursor: 'not-allowed',
            },
            '*::placeholder': {
                color: `${inputSpecs.disabled.color.default} !important`,
            },
        },
    })
}

// eslint-disable-next-line complexity
const InputNew = ({
    id,
    name,
    disabled,
    inferred,
    readOnly,
    error,
    hint,
    placeholder,
    type,
    onChange,
    onKeyDown,
    onBlur,
    onFocus,
    className,
    wide,
    percentage,
    startAdornment,
    endAdornment,
    sx,
    highlightOnFocus,
    autoFocus,
    tabIndex,
    size = 'md',
    ...rest
}: {
    id?: string
    disabled?: boolean
    inferred?: boolean
    readOnly?: boolean
    error?: string | boolean
    hint?: string
    placeholder?: string
    type?: string
    name?: string
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>
    onBlur?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    value?: string | number
    percentage?: boolean
    className?: string
    wide?: boolean
    startAdornment?: React.ReactNode
    endAdornment?: React.ReactNode
    sx?: SxProps
    highlightOnFocus?: boolean
    autoFocus?: boolean
    tabIndex?: number
    size?: 'sm' | 'md'
}) => {
    const StyledInput = useMemo(
        () => styled(MuiInput)(getInputBaseStyles(size, wide, 'input')),
        [wide, size],
    )

    const StyledNumberFormat = useMemo(
        () =>
            styled(NumberFormat)(
                LuneThemeNew.unstable_sx({
                    width: `100%`,
                    height: `100%`,
                    border: 'none',
                    backgroundColor: 'transparent',
                    '&:focus': { outline: `none;` },
                }),
            ),
        [],
    )

    const NumberFormatPercent = useMemo(
        () =>
            forwardRef<
                NumberFormat<any>,
                {
                    onChange: (event: { target: { name: string; value: string } }) => void
                    onBlur: (event: { target: { name: string; value: string } }) => void
                    name: string
                }
            >(function NumberFormatCustom(props, ref) {
                const { onChange, onBlur, ...other } = props
                return (
                    <StyledNumberFormat
                        allowNegative={false}
                        allowEmptyFormatting={true}
                        allowLeadingZeros={false}
                        defaultValue={0}
                        onBlur={onBlur}
                        isAllowed={(values) => {
                            const { floatValue } = values
                            if (!floatValue) {
                                return true
                            }
                            return floatValue >= 0 && floatValue <= 100
                        }}
                        suffix="%"
                        {...other}
                        getInputRef={ref}
                        onValueChange={(values) => {
                            onChange({
                                target: {
                                    name: ``,
                                    value: values.value,
                                },
                            })
                        }}
                    />
                )
            }),
        [StyledNumberFormat],
    )

    return (
        <>
            <StyledInput
                autoFocus={autoFocus}
                onKeyDown={onKeyDown}
                {...(id && { id })}
                {...(placeholder && { placeholder })}
                {...(onChange && { onChange })}
                {...(onBlur && { onBlur })}
                {...(name && { name })}
                inputProps={{ tabIndex: tabIndex || 0 }}
                onFocus={(e) => {
                    if (highlightOnFocus) {
                        e.target.select()
                    }
                    onFocus?.(e)
                }}
                className={`${className} ${
                    disabled
                        ? 'disabled'
                        : readOnly
                          ? 'readOnly'
                          : error
                            ? 'error'
                            : inferred
                              ? 'inferred'
                              : ''
                }`}
                type={type ?? 'text'}
                disabled={(disabled || readOnly) ?? false}
                disableUnderline={true}
                sx={
                    type === `number` || percentage
                        ? {
                              ...sx,
                              input: { textAlign: 'right' },
                          }
                        : sx
                }
                {...(percentage && {
                    inputComponent: NumberFormatPercent as any,
                })}
                {...(startAdornment && {
                    startAdornment: (
                        <InputAdornment position="start">{startAdornment}</InputAdornment>
                    ),
                })}
                {...(endAdornment && {
                    endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment>,
                })}
                {...rest}
            />
            {(!error || typeof error !== `string`) && hint && (
                <Typography
                    variant={'caption'}
                    sx={{
                        display: 'block',
                        color: palette.Grey600,
                        mt: 1,
                    }}
                >
                    {hint}
                </Typography>
            )}
            {typeof error === `string` && <FieldValidationError error={error} />}
        </>
    )
}

export default InputNew
